import { useEffect } from "react";

import { useAppContext, useAppDispatch } from "@app/helpers/hooks";
import { isRoundtrip, isReturn } from "@app/helpers/search";
import { useResultsSelector } from "@app/modules/results/hooks/use-results-selector";
import { getSearch } from "@app/modules/results/selectors";
import { getTripDetailsFetchStatus } from "@app/modules/results/selectors/state-accessors";
import { getSegmentsWithUnknownVehicleLayout } from "@app/modules/results/selectors/trips/get-segments-with-unknown-vehicle-layout";
import { getTripDetailsThunk } from "@app/modules/results/state/thunks/get-trip-details";
import type { ResultsReduxState } from "@app/types/results-redux-types";
import type { SearchInfo } from "@app/types/search-types";

/**
 *
 * @param trip_ids
 * @param disabled used on ticket option page. It is used to prevent the fetch if SSF XP isn't enabled
 */
export const useFetchSegmentVehicleIfNotLoaded = (
  trip_ids: string[] | undefined,
  disabled: boolean = false
) => {
  const dispatch = useAppDispatch();
  const { api } = useAppContext();
  const search = useResultsSelector<SearchInfo>(getSearch);
  const trip_details_fetch_status = useResultsSelector(
    getTripDetailsFetchStatus
  );

  const trips_to_fetch_vehicle = useResultsSelector(
    (state: ResultsReduxState) => {
      if (!trip_ids) {
        return [];
      }

      return trip_ids.filter((trip_id: string) => {
        return getSegmentsWithUnknownVehicleLayout(state, trip_id).length > 0;
      });
    }
  );

  const is_outbound_leg_of_roundtrip_search =
    isRoundtrip(search) && !isReturn(search);

  useEffect(() => {
    if (
      disabled ||
      is_outbound_leg_of_roundtrip_search ||
      trips_to_fetch_vehicle.length === 0
    ) {
      return;
    }

    trips_to_fetch_vehicle.forEach(trip_id => {
      if (!trip_details_fetch_status[trip_id]) {
        dispatch(
          getTripDetailsThunk({
            api,
            trip_id
          })
        );
      }
    });
  }, [
    api,
    disabled,
    dispatch,
    trip_details_fetch_status,
    is_outbound_leg_of_roundtrip_search,
    trip_ids,
    trips_to_fetch_vehicle
  ]);

  return { trips_to_fetch_vehicle };
};
